import cx from 'classnames';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { icons } from '../assets';
import { BigButton, ModalButtonArea } from '../common';
import { MAX_PASSWORD_LENGTH } from '../constants/projectConstants';
import {
  ADMIN_DASHBOARD,
  PRIVACY_POLICY_ROUTE,
  TERMS_OF_USE_ROUTE,
} from '../constants/routePathConstants';
import { useUserContext } from '../contexts/UserContext';
import { apiChangePassword } from '../helpers/api';
import { objectToFormData, windowOpen } from '../helpers/jsHelper';
import './Login.scss';
import { Formik } from 'formik';
import { Col, Form, Row } from 'react-bootstrap';
import { RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import welcomeImg from '../assets/images/welcome-img.webp';
import { selectResetPopOpen, setResetPasswordPopupOpen } from '../redux/slicers/uiSlicer';
import styles from './guestUserResetPasswordPopup.module.scss';

const { ExclamationIcon } = icons;

export function GuestUserResetPasswordPopup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser } = useUserContext();
  const isModalOpen = useSelector(selectResetPopOpen);
  const [showPassword, setShowPassword] = useState({
    confirmPassword: false,
    password: false,
  });
  const [loading, setLoading] = useState(false);

  const onClose = () => {
    window.$recentPassword = null
    dispatch(setResetPasswordPopupOpen(false));
  };

  const initialValues = {
    email: '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string().required('Password is required'),
    confirmPassword: Yup.string()
      .required('Confirm Password is required')
      .oneOf([Yup.ref('password')], 'Confirm password must match with the password'),
  });

  const onSubmit = (values) => {
    if (window.$recentPassword) {
      setLoading(true);
      const payload = {
        old_password: window.$recentPassword,
        new_password: values?.password,
      };
      apiChangePassword(objectToFormData(payload))
        .then((response) => {
          if (response) {
            onClose();
            window.$recentPassword = null;
            navigate(ADMIN_DASHBOARD);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      window.$recentPassword = null;
      onClose();
      navigate(ADMIN_DASHBOARD);
    }
  };

  const settings = {
    initialValues,
    validationSchema,
    onSubmit,
  };

  const handleShowPassword = (e) => {
    setShowPassword({
      ...showPassword,
      ...e,
    });
  };

  const redirect = (url) => {
    url && windowOpen(url, '_blank');
  };

  if (!isModalOpen) return null;

  return (
    <>
      <ModalButtonArea
        showModal={isModalOpen}
        hideCloseButton={true}
        removeCloseBtn={true}
        setShowModal={onClose}
        size="xl"
        startOpen={true}
        centered={true}
        bigButton={false}
        bigIcon={true}
        variant="link"
        onClose={onClose}
        className={styles.welcomeModal}
        backdropClassName="overlay-dark"
        headingLeft
        body={
          <>
            <Row className="g-0">
              <Col md={6}>
                <div className={styles.welcomeModalContent}>
                  <h4>Welcome to imagine.io</h4>
                  <p>Please create a password to access your account later.</p>
                  <Formik {...settings}>
                    {({
                      values,
                      errors,
                      touched,
                      isSubmitting,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      setTouched,
                    }) => (
                      <Form
                        onKeyDown={(e) => {
                          const code = e?.keyCode || e?.which;
                          if (code === 13) {
                            e.preventDefault();
                            handleSubmit();
                          }
                        }}
                        className="w-100">
                        {currentUser?.user?.email && (
                          <Form.Group className={styles.formGroup}>
                            {errors.email && touched.email ? (
                              <Form.Label className="text-danger">
                                <ExclamationIcon />
                                <span>{errors.email}</span>
                              </Form.Label>
                            ) : null}
                            <Form.Control
                              type="text"
                              name="email"
                              className={cx(errors.email && touched.email && 'input-error')}
                              onChange={handleChange}
                              onFocus={() => setTouched({ ...touched, email: false })}
                              onBlur={handleBlur}
                              placeholder="Email"
                              disabled={true}
                              value={currentUser?.user?.email}
                            />
                          </Form.Group>
                        )}
                        <Form.Group className={styles.formGroup}>
                          <div className="password-field">
                            {errors.password && touched.password ? (
                              <Form.Label className="text-danger pwd-overflow">
                                <ExclamationIcon />
                                <span>{errors.password}</span>
                              </Form.Label>
                            ) : null}
                            <Form.Control
                              type={showPassword.password ? 'text' : 'password'}
                              name="password"
                              className={cx(errors.password && touched.password && 'input-error')}
                              onChange={handleChange}
                              onFocus={() => setTouched({ ...touched, password: false })}
                              onBlur={handleBlur}
                              placeholder="Enter password*"
                              value={values.password}
                              maxLength={MAX_PASSWORD_LENGTH}
                            />
                            {showPassword.password ? (
                              <RiEyeLine
                                className="eye-icon"
                                onClick={() => handleShowPassword({ password: false })}
                              />
                            ) : (
                              <RiEyeOffLine
                                className="eye-icon"
                                onClick={() => handleShowPassword({ password: true })}
                              />
                            )}
                          </div>
                        </Form.Group>
                        <Form.Group className={styles.formGroup}>
                          <div className="password-field">
                            {errors.confirmPassword && touched.confirmPassword ? (
                              <Form.Label className="text-danger pwd-overflow">
                                <ExclamationIcon />
                                <span>{errors.confirmPassword}</span>
                              </Form.Label>
                            ) : null}
                            <Form.Control
                              type={showPassword.confirmPassword ? 'text' : 'password'}
                              name="confirmPassword"
                              className={cx(
                                errors.confirmPassword && touched.confirmPassword && 'input-error'
                              )}
                              onChange={handleChange}
                              onFocus={() => setTouched({ ...touched, confirmPassword: false })}
                              onBlur={handleBlur}
                              placeholder="Confirm Password*"
                              value={values.confirmPassword}
                              maxLength={MAX_PASSWORD_LENGTH}
                            />
                            {showPassword.confirmPassword ? (
                              <RiEyeLine
                                className="eye-icon"
                                onClick={() => handleShowPassword({ confirmPassword: false })}
                              />
                            ) : (
                              <RiEyeOffLine
                                className="eye-icon"
                                onClick={() => handleShowPassword({ confirmPassword: true })}
                              />
                            )}
                          </div>
                        </Form.Group>
                        <Form.Group className="form-btn mb-3">
                          <BigButton
                            type="button"
                            disabled={isSubmitting}
                            className="bigButton lf-btn-1 w-100"
                            buttonTitle="Continue"
                            onClick={handleSubmit}
                            isLoading={loading}
                          />
                        </Form.Group>
                      </Form>
                    )}
                  </Formik>
                  <p>
                    By continuing, you agree to our{' '}
                    <a href="#" onClick={() => redirect(TERMS_OF_USE_ROUTE)}>
                      Terms of Services
                    </a>{' '}
                    and{' '}
                    <a href="#" onClick={() => redirect(PRIVACY_POLICY_ROUTE)}>
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </Col>
              <Col>
                <img className={styles.welcomeImage} src={welcomeImg} />
              </Col>
            </Row>
          </>
        }
      />
    </>
  );
}
